import { Head } from '@inertiajs/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SplitFlapText } from './SplitFlap';

/**
 * TASK-067 — andr.emovia.com Easter Egg.
 *
 * A Solari-style split-flap departures board. Everyday annoyances are listed
 * as cancelled/suppressed trains; only "ANDREMO VIA — IN PARTENZA" is leaving.
 * Interacting (click/Enter on the special row, or 25s idle) triggers the pun
 * moment: the board recomposes into ANDR.EMOVIA and reveals a single CTA.
 *
 * SSR-safe: the first render is fully deterministic (static pool order, no
 * clock, no random). Clock, rotation and randomness start only after mount.
 */

type Status = 'CANCELLATO' | 'SOPPRESSO' | 'IN RITARDO' | 'DEVIATO' | 'IN PARTENZA';

type Destination = {
  name: string;
  status: Status;
  /**
   * Abbreviated label for narrow viewports where DEST_COLS_COMPACT cells fit.
   * Only needed for entries longer than DEST_COLS_COMPACT chars.
   * Must be ≤ DEST_COLS_COMPACT chars and remain semantically clear.
   */
  short?: string;
};

// Approved pool (TASK-067). Do not add clinical terms or product claims.
// `short` entries are intentional ellipses for mobile — never mid-word cuts.
const POOL: Destination[] = [
  { name: 'RUMORE DI FONDO', status: 'CANCELLATO' },
  { name: 'PENSIERI DELLE 3 DI NOTTE', status: 'SOPPRESSO', short: 'PENSIERI DELLE 3' },
  { name: 'DOMENICA SERA', status: 'IN RITARDO' },
  { name: 'RIUNIONI INUTILI', status: 'CANCELLATO' },
  { name: 'NOTIFICHE', status: 'SOPPRESSO' },
  { name: '"VA TUTTO BENE"', status: 'DEVIATO' },
  { name: "SCUSE DELL'ULTIMO MINUTO", status: 'CANCELLATO', short: "SCUSE DELL'ULTIMO" },
  { name: 'LUNEDÌ MATTINA', status: 'IN RITARDO' },
  { name: 'PERFEZIONISMO', status: 'SOPPRESSO' },
  { name: 'FRETTA', status: 'DEVIATO' },
  { name: 'GIUDIZI NON RICHIESTI', status: 'CANCELLATO', short: 'GIUDIZI NON RICH.' },
  { name: 'CORSE A VUOTO', status: 'SOPPRESSO' },
  { name: 'FACCIA DA CIRCOSTANZA', status: 'DEVIATO', short: 'FACCIA DA CIRCOST.' },
  { name: 'RIMUGINIO', status: 'CANCELLATO' },
  { name: 'TRAFFICO MENTALE', status: 'IN RITARDO' },
  { name: 'TUTTO E SUBITO', status: 'SOPPRESSO' },
];

const SPECIAL: Destination = { name: 'ANDREMO VIA', status: 'IN PARTENZA' };

// Board geometry — derived from pool so adding a new entry never silently truncates.
const ROWS = 8;
const BIN_COLS = 2;

// Full-width destination column: longest name in the pool (incl. SPECIAL), no extra margin
// needed since SplitFlapText pads to exactly this length.
const DEST_COLS = Math.max(...[...POOL, SPECIAL].map((d) => d.name.length));

// Compact column for narrow viewports: longest `short` label (fallback to name if no short).
const DEST_COLS_COMPACT = Math.max(
  ...[...POOL, SPECIAL].map((d) => (d.short ?? d.name).length),
);

// Status column: longest status string in the approved set.
const STATUS_COLS = Math.max(...[...POOL, SPECIAL].map((d) => d.status.length));

// Viewport width (px) below which we switch to compact destination labels.
// Set high enough that 25 full-length cells always fit above this threshold
// (board max-width = 960px, fits comfortably at ≥ 960px).
// Below this point `Destination.short` labels (≤ 18 chars) are used instead.
const COMPACT_BP = 900;
const IDLE_MS = 25000;

const STATUS_TONE: Record<Status, string> = {
  CANCELLATO: 'status-bad',
  SOPPRESSO: 'status-bad',
  'IN RITARDO': 'status-warn',
  DEVIATO: 'status-warn',
  'IN PARTENZA': 'status-go',
};

// Deterministic initial board: special row first, then the first ROWS-1 of pool.
function initialRows(): Destination[] {
  return [SPECIAL, ...POOL.slice(0, ROWS - 1)];
}

/** True when the viewport is narrow enough to use compact destination labels. SSR-safe: starts false. */
function useCompact(): boolean {
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${COMPACT_BP - 1}px)`);
    setCompact(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setCompact(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return compact;
}

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

function useClock(active: boolean): string {
  const [time, setTime] = useState('');
  useEffect(() => {
    if (!active) return;
    const tick = () => {
      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      setTime(`${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [active]);
  return time;
}

const BoardRow = ({
  row,
  special,
  reducedMotion,
  compact,
}: {
  row: Destination;
  special: boolean;
  reducedMotion: boolean;
  compact: boolean;
}) => {
  // On narrow viewports use the intentional abbreviation when available,
  // never a raw character-count truncation.
  const destCols = compact ? DEST_COLS_COMPACT : DEST_COLS;
  const destText = compact && row.short ? row.short : row.name;

  return (
    <div className={`board-row${special ? ' board-row-special' : ''}`}>
      <span className="board-bin" aria-hidden="true">
        {/* Special row: bin "1"; others: "--" so flaps are never empty. */}
        <SplitFlapText
          text={special ? ' 1' : '--'}
          length={BIN_COLS}
          reducedMotion={reducedMotion}
          align="right"
        />
      </span>
      <span className="board-dest">
        <SplitFlapText text={destText} length={destCols} reducedMotion={reducedMotion} />
      </span>
      <span className={`board-status ${STATUS_TONE[row.status]}${special ? ' board-status-blink' : ''}`}>
        <SplitFlapText text={row.status} length={STATUS_COLS} reducedMotion={reducedMotion} align="right" />
      </span>
    </div>
  );
};

const PunReveal = ({ reducedMotion }: { reducedMotion: boolean }) => {
  // Stage 0: ANDREMO VIA. Stage 1: flip to ANDR.EMOVIA. Then question + CTA.
  const [text, setText] = useState(reducedMotion ? 'ANDR.EMOVIA' : 'ANDREMO VIA ');
  const [question, setQuestion] = useState(reducedMotion ? 'Tu, da cosa andresti via?' : '');
  const [showCta, setShowCta] = useState(reducedMotion);

  const typerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (reducedMotion) return;
    const flip = setTimeout(() => setText('ANDR.EMOVIA'), 700);
    const full = 'Tu, da cosa andresti via?';
    let typed = 0;
    const startTyping = setTimeout(() => {
      typerRef.current = setInterval(() => {
        typed += 1;
        setQuestion(full.slice(0, typed));
        if (typed >= full.length && typerRef.current) {
          clearInterval(typerRef.current);
          typerRef.current = null;
          setShowCta(true);
        }
      }, 55);
    }, 2200);
    return () => {
      clearTimeout(flip);
      clearTimeout(startTyping);
      if (typerRef.current) clearInterval(typerRef.current);
    };
  }, [reducedMotion]);

  return (
    <div className="pun-stage">
      <h1 className="sr-only">andr.emovia — Scopri Emovia</h1>
      <div className="pun-title" aria-hidden="true">
        <SplitFlapText text={text} length={11} reducedMotion={reducedMotion} flipMs={70} />
      </div>
      <p className="pun-question" aria-hidden={!showCta}>
        {question}
        {!reducedMotion && !showCta && <span className="pun-caret">|</span>}
      </p>
      <a
        className={`pun-cta${showCta ? ' pun-cta-on' : ''}`}
        href="https://www.emovia.com"
      >
        Scopri Emovia
        <span aria-hidden="true" className="pun-cta-arrow">
          →
        </span>
      </a>
    </div>
  );
};

const Andr = () => {
  const reducedMotion = useReducedMotion();
  const compact = useCompact();
  const [mounted, setMounted] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [rows, setRows] = useState<Destination[]>(initialRows);
  const clock = useClock(mounted && !revealed);
  const poolCursorRef = useRef(ROWS - 1);

  useEffect(() => setMounted(true), []);

  const reveal = useCallback(() => setRevealed(true), []);

  // Auto-rotate non-special rows once mounted; idle timer triggers the pun.
  useEffect(() => {
    if (!mounted || revealed || reducedMotion) return;

    let active = true;
    let rotate: ReturnType<typeof setTimeout> | null = null;
    const scheduleRotate = () => {
      const delay = 4000 + Math.random() * 3000;
      rotate = setTimeout(() => {
        if (!active) return;
        setRows((current) => {
          // Rotate a random non-special slot (index 0 is the special row).
          const slot = 1 + Math.floor(Math.random() * (current.length - 1));
          const next = POOL[poolCursorRef.current % POOL.length];
          poolCursorRef.current += 1;
          const copy = [...current];
          copy[slot] = next;
          return copy;
        });
        scheduleRotate();
      }, delay);
    };
    scheduleRotate();

    const idle = setTimeout(reveal, IDLE_MS);
    return () => {
      active = false;
      if (rotate) clearTimeout(rotate);
      clearTimeout(idle);
    };
  }, [mounted, revealed, reducedMotion, reveal]);

  const onSpecialActivate = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      if ('key' in e && e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      reveal();
    },
    [reveal],
  );

  return (
    <div className="andr-root">
      <Head title="andremovia" />
      <div className="andr-vignette" aria-hidden="true" />
      <div className="andr-scanlines" aria-hidden="true" />

      <main className="andr-stage">
        {!revealed ? (
          <section className="board" aria-label="Tabellone partenze">
            <header className="board-head">
              <div className="board-head-titles">
                <span className="board-head-it">PARTENZE</span>
                <span className="board-head-en">DEPARTURES</span>
              </div>
              <time className="board-clock" aria-live="off">
                {clock || ' '}
              </time>
            </header>

            <div className="board-cols" aria-hidden="true">
              <span>BIN</span>
              <span>DESTINAZIONE</span>
              <span className="board-cols-status">STATO</span>
            </div>

            <div className="board-body">
              {rows.map((row, i) =>
                i === 0 ? (
                  <div
                    key="special"
                    role="button"
                    tabIndex={0}
                    className="board-row-trigger"
                    aria-label="ANDREMO VIA, binario 1, in partenza. Scopri Emovia."
                    onClick={onSpecialActivate}
                    onKeyDown={onSpecialActivate}
                  >
                    <BoardRow row={row} special reducedMotion={reducedMotion} compact={compact} />
                  </div>
                ) : (
                  <BoardRow key={`row-${i}`} row={row} special={false} reducedMotion={reducedMotion} compact={compact} />
                ),
              )}
            </div>

            {/* CTA always reachable by keyboard, even before the pun moment. */}
            <a className="board-skip" href="https://www.emovia.com">
              Scopri Emovia
            </a>
          </section>
        ) : (
          <PunReveal reducedMotion={reducedMotion} />
        )}
      </main>

      <style>{STYLES}</style>
    </div>
  );
};

const STYLES = `
.andr-root {
  position: fixed;
  inset: 0;
  overflow: hidden;
  background:
    radial-gradient(120% 90% at 50% 0%, #14181c 0%, #0a0c0e 55%, #050607 100%);
  color: #f4d58d;
  font-family: ui-monospace, "SF Mono", "JetBrains Mono", Menlo, Consolas, monospace;
}
.andr-vignette {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(120% 100% at 50% 40%, transparent 55%, rgba(0,0,0,0.65) 100%);
}
.andr-scanlines {
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.35;
  background: repeating-linear-gradient(
    to bottom,
    rgba(255,255,255,0.025) 0px,
    rgba(255,255,255,0.025) 1px,
    transparent 1px,
    transparent 3px
  );
}
.andr-stage {
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(0.75rem, 3vw, 2.5rem);
}

/* ---- Board ---- */
.board {
  width: min(960px, 100%);
  display: flex;
  flex-direction: column;
  gap: clamp(0.5rem, 1.5vw, 1rem);
}
.board-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  border-bottom: 2px solid rgba(244, 213, 141, 0.25);
  padding: 0 var(--board-pad) clamp(0.4rem, 1.5vw, 0.9rem);
}
.board-head-titles {
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  flex-wrap: wrap;
}
.board-head-it {
  font-size: clamp(1.1rem, 4.5vw, 2rem);
  font-weight: 700;
  letter-spacing: 0.12em;
  color: #ffe6a8;
  text-shadow: 0 0 14px rgba(255, 198, 88, 0.35);
}
.board-head-en {
  font-size: clamp(0.6rem, 2vw, 0.9rem);
  letter-spacing: 0.3em;
  color: rgba(244, 213, 141, 0.5);
}
.board-clock {
  font-variant-numeric: tabular-nums;
  font-size: clamp(1rem, 4vw, 1.9rem);
  letter-spacing: 0.08em;
  color: #aef6b5;
  text-shadow: 0 0 12px rgba(120, 245, 150, 0.4);
}
/*
 * Column widths are derived from the flap cell size so they never collide.
 * --board-col-bin  = BIN_COLS(2)    × flap-w + 1 gap
 * --board-col-stat = STATUS_COLS(11) × flap-w + 10 gaps
 * flap-w = clamp(0.8rem, 2.8vw, 1.35rem), gap between cells = 2px
 *
 * Desktop: DEST_COLS=24 cells, no truncation.
 * Mobile (<640px): DEST_COLS_COMPACT=18 cells via JS compact mode;
 *   flap-w also shrinks via clamp so column widths stay consistent.
 */
:root {
  --flap-w-val: clamp(0.8rem, 2.8vw, 1.35rem);
  --board-col-bin:  calc(2  * var(--flap-w-val) + 2px);
  --board-col-stat: calc(11 * var(--flap-w-val) + 20px);
  --board-gap: clamp(0.4rem, 1.5vw, 0.8rem);
  --board-pad: clamp(0.4rem, 1.2vw, 0.75rem);
}
@media (max-width: 899px) {
  :root {
    --board-gap: 0.3rem;
    --board-pad: 0.4rem;
  }
}
@media (max-width: 479px) {
  :root {
    --board-gap: 0.2rem;
    --board-pad: 0.3rem;
    --flap-w-val: clamp(0.65rem, 2.6vw, 0.8rem);
  }
}
.board-cols {
  display: grid;
  grid-template-columns: var(--board-col-bin) 1fr var(--board-col-stat);
  gap: var(--board-gap);
  font-size: clamp(0.5rem, 1.6vw, 0.65rem);
  letter-spacing: 0.22em;
  color: rgba(244, 213, 141, 0.4);
  padding: 0 var(--board-pad);
}
.board-cols-status { text-align: right; }
.board-body {
  display: flex;
  flex-direction: column;
  gap: clamp(0.2rem, 0.8vw, 0.4rem);
}
.board-row {
  display: grid;
  grid-template-columns: var(--board-col-bin) 1fr var(--board-col-stat);
  gap: var(--board-gap);
  align-items: center;
  background: linear-gradient(180deg, #131517 0%, #0c0d0f 100%);
  border-radius: 5px;
  padding: clamp(0.2rem, 0.7vw, 0.4rem) var(--board-pad);
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,0.04),
    0 1px 2px rgba(0,0,0,0.5);
}
.board-row-trigger {
  cursor: pointer;
  border-radius: 6px;
  outline: none;
}
.board-row-trigger:focus-visible {
  box-shadow: 0 0 0 2px #0a0c0e, 0 0 0 4px #aef6b5;
}
.board-row-special {
  background: linear-gradient(180deg, #1a1d18 0%, #10120d 100%);
  box-shadow:
    inset 0 0 0 1px rgba(174, 246, 181, 0.25),
    0 0 22px rgba(120, 245, 150, 0.12);
}
.board-bin { color: #ffe6a8; overflow: hidden; }
.board-dest { color: #f4d58d; min-width: 0; overflow: hidden; }
.board-status { font-weight: 600; overflow: hidden; display: flex; justify-content: flex-end; }
.status-bad { color: #ff8a7a; }
.status-warn { color: #ffd166; }
.status-go { color: #aef6b5; text-shadow: 0 0 10px rgba(120, 245, 150, 0.5); }
.board-status-blink { animation: andr-blink 1.1s steps(2, start) infinite; }
@keyframes andr-blink { 50% { opacity: 0.25; } }

.board-skip {
  position: absolute;
  left: -9999px;
  top: 0;
}
.board-skip:focus-visible {
  position: static;
  align-self: center;
  margin-top: 0.5rem;
  color: #aef6b5;
  text-decoration: underline;
}

/* ---- Flap mechanism ---- */
/*
 * Each .flap cell is a fixed box (width × height). Inside, two .flap-half
 * panels each occupy exactly 50% height with overflow:hidden. Inside each
 * half lives a .flap-inner div sized at 200% of the half height (= 100% of
 * the full flap height), positioned so the correct vertical slice of the
 * glyph shows through the overflow clamp.
 *
 * Top half    → inner at top:0      → glyph top slice visible
 * Bottom half → inner at bottom:0   → glyph bottom slice visible
 *
 * This guarantees the character is bisected at the hinge line with zero gap
 * and no empty band below.
 */
.flap-row {
  display: inline-flex;
  gap: 2px;
}
.flap {
  --flap-h: clamp(1.25rem, 4.5vw, 2rem);
  --flap-w: clamp(0.8rem, 2.8vw, 1.35rem);
  --flap-fs: clamp(0.72rem, 3vw, 1.3rem);
  position: relative;
  display: inline-block;
  width: var(--flap-w);
  height: var(--flap-h);
  font-size: var(--flap-fs);
  font-weight: 700;
  line-height: 1;
  perspective: 120px;
  flex-shrink: 0;
}
.flap-half {
  position: absolute;
  left: 0;
  right: 0;
  overflow: hidden;
  background: linear-gradient(180deg, #1c1f22 0%, #15181b 100%);
}
.flap-top {
  top: 0;
  height: 50%;
  border-radius: 3px 3px 0 0;
  border-bottom: 1px solid rgba(0,0,0,0.75);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.06);
}
.flap-bottom {
  bottom: 0;
  height: 50%;
  border-radius: 0 0 3px 3px;
  box-shadow: inset 0 -1px 0 rgba(0,0,0,0.4);
}
/*
 * .flap-inner: a box that is 200% of the half-height = 100% of full flap.
 * It holds the glyph centered in that full height via flex.
 * Top half: inner starts at top → top slice shows.
 * Bottom half: inner starts at bottom (negative offset) → bottom slice shows.
 */
.flap-inner {
  position: absolute;
  left: 0;
  right: 0;
  height: 200%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
}
.flap-top .flap-inner {
  top: 0;
}
.flap-bottom .flap-inner {
  bottom: 0;
}
.flap-glyph {
  display: block;
  line-height: 1;
  white-space: pre;
}
/* Falling flap: the top-half panel that rotates down (outgoing glyph). */
.flap-fold {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 50%;
  transform-origin: bottom center;
  transform-style: preserve-3d;
  z-index: 2;
  animation-name: andr-fold;
  animation-timing-function: ease-in;
  animation-fill-mode: forwards;
}
.flap-fold .flap-fold-top {
  border-radius: 3px 3px 0 0;
  background: linear-gradient(180deg, #23282e 0%, #181c20 100%);
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,0.07),
    0 3px 8px rgba(0,0,0,0.7);
}
@keyframes andr-fold {
  0%   { transform: rotateX(0deg); }
  100% { transform: rotateX(-90deg); }
}

/* ---- Pun reveal ---- */
.pun-stage {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(1.5rem, 5vw, 3rem);
  text-align: center;
  animation: andr-fade-in 0.6s ease both;
  padding: 0 clamp(0.5rem, 3vw, 2rem);
}

/*
 * Pun title cells are 2.5× the board cells.
 * Clamp lower bounds are computed for 320px (11 cells + 10×1px gap):
 *   available ≈ 320 - 2×12px(stage-padding) = 296px
 *   w_min = (296 - 10×1px) / 11 ≈ 26px ≈ 1.6rem  → use 1.4rem for safety
 * Upper bounds: 10.5rem (w) / 15rem (h) / 9.5rem (fs) ≈ 2.5× board max.
 *
 * Border-radius, hinge line, perspective, and drop-shadow are also scaled
 * proportionally so the cells don't look pixelated at large sizes.
 */
/*
 * Pun title: override flap geometry directly on .pun-title .flap so the
 * more-specific rule wins over the base .flap definition.
 * border-radius is intentionally NOT scaled — Solari flaps are rectangular
 * with only a slight chamfer. Fixed 4px at any viewport size.
 * Hinge seam stays 2px; no scaling.
 */
.pun-title .flap {
  --flap-w:  clamp(1.4rem, 7.5vw, 10.5rem);
  --flap-h:  clamp(2rem,   11vw,  15rem);
  --flap-fs: clamp(1.3rem, 7vw,   9.5rem);
  perspective: clamp(120px, 18vw, 800px);
}
.pun-title .flap-row {
  gap: clamp(1px, 0.3vw, 4px);
}
.pun-title .flap-top {
  border-radius: 4px 4px 0 0;
  border-bottom: 2px solid rgba(0,0,0,0.85);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.07);
}
.pun-title .flap-bottom {
  border-radius: 0 0 4px 4px;
  box-shadow: inset 0 -1px 0 rgba(0,0,0,0.5);
}
.pun-title .flap-fold .flap-fold-top {
  border-radius: 4px 4px 0 0;
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,0.08),
    0 6px 24px rgba(0,0,0,0.85);
}

/* EMOVIA in brand color: cells 6..11 of the 11-cell reveal title. */
.pun-title .flap-row .flap:nth-child(n + 6) {
  color: #6ea8ff;
}

.pun-question {
  min-height: 1.4em;
  font-size: clamp(1rem, 3vw, 1.6rem);
  letter-spacing: 0.04em;
  color: rgba(244, 213, 141, 0.85);
}
.pun-caret { animation: andr-blink 0.9s steps(2, start) infinite; }
.pun-cta {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: clamp(0.75rem, 1.5vw, 1rem) clamp(1.4rem, 3vw, 2.2rem);
  border-radius: 999px;
  font-size: clamp(1rem, 2.8vw, 1.35rem);
  font-weight: 700;
  letter-spacing: 0.03em;
  color: #fff;
  background: hsl(225 100% 40%);
  box-shadow: 0 8px 30px hsl(225 100% 40% / 0.45);
  text-decoration: none;
  opacity: 0;
  transform: translateY(12px);
  transition: opacity 0.5s ease, transform 0.5s ease, box-shadow 0.25s ease;
  pointer-events: none;
}
.pun-cta-on { opacity: 1; transform: translateY(0); pointer-events: auto; }
.pun-cta:hover { box-shadow: 0 10px 38px hsl(225 100% 40% / 0.6); }
.pun-cta:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px #0a0c0e, 0 0 0 6px #aef6b5;
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}
.pun-cta-arrow { transition: transform 0.2s ease; }

@keyframes andr-fade-in { from { opacity: 0; transform: translateY(10px); } }

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (prefers-reduced-motion: reduce) {
  .board-status-blink,
  .pun-caret,
  .pun-stage { animation: none; }
  .pun-cta { opacity: 1; transform: none; pointer-events: auto; }
}
`;

export default Andr;
